import { calculatePercentChange, formatPercent } from "@bridgesplit/utils";
import { BS_SHORT_URL_INVITE } from "app/constants";
import {
    calculateLeverageMultiplier,
    formatLeverage,
    getLeverageLoopDirection,
    isHistoricalPrincipalWithdrawal,
    LoopPositionExpanded,
    UserVaultPnl,
    UserVaultPositionExpanded,
    useUserReferral,
    useVaultApyBreakdown
} from "@bridgesplit/abf-react";
import { LoopDisplayType, useLoopProfitLoss, useProfitLossDetails } from "app/components/common";
import { toPng } from "html-to-image";

import { OverlayConfig, OverlayPalette, OverlayProps, SocialShareDialogProps, SocialShareStat } from "./types";
import { SOCIAL_SHARE_CARD } from "./constants";

export function getOverlaysWithPalette({
    overlays,
    palette
}: {
    overlays: OverlayConfig[];
    palette: OverlayPalette;
}): OverlayProps[] {
    return overlays.map(({ id, render }) => ({
        id,
        svg: render({ palette })
    }));
}

export function calculateStatsXPos(stats: SocialShareStat[]): [number, number, number] {
    const gap = 48;
    const maxWidths = stats.map(({ label, value }) => Math.max(label.length * 12, value.length * 18));
    const x1 = 48;
    const x2 = x1 + maxWidths[0] + gap;
    const x3 = x2 + maxWidths[1] + gap;
    return [x1, x2, x3];
}

async function inlineImages(svg: HTMLElement) {
    const images = svg.querySelectorAll("image");
    for (const img of Array.from(images)) {
        const href = img.getAttribute("href");
        if (href && !href.startsWith("data:")) {
            try {
                const resp = await fetch(href, { mode: "cors" });
                const blob = await resp.blob();
                const reader = new FileReader();
                const dataUrl: string = await new Promise((resolve) => {
                    reader.onload = () => resolve(reader.result as string);
                    reader.readAsDataURL(blob);
                });
                img.setAttribute("href", dataUrl);
            } catch (e) {
                return;
            }
        }
    }
}

export async function exportSvgtoPng(svgRef: React.RefObject<HTMLElement>): Promise<string | undefined> {
    if (!svgRef.current) return;

    // Inline any <image> tags as base64 so html-to-image has no CORS issues
    await inlineImages(svgRef.current);

    try {
        // Use html-to-image to handle serialization + rasterization
        const dataUrl = await toPng(svgRef.current, {
            cacheBust: true,
            canvasWidth: SOCIAL_SHARE_CARD.WIDTH,
            canvasHeight: SOCIAL_SHARE_CARD.HEIGHT,
            pixelRatio: window.devicePixelRatio || 2
        });
        return dataUrl;
    } catch (err) {
        return;
    }
}

/**
 * Hook to get the social share props for a loop position.
 */
export function useLoopSocialShareProps(
    loopPosition: LoopPositionExpanded | undefined,
    gainSummary: ReturnType<typeof useLoopProfitLoss>,
    displayType: LoopDisplayType
): SocialShareDialogProps | undefined {
    const { data: referral } = useUserReferral();

    const principalWithdrawal = isHistoricalPrincipalWithdrawal(loopPosition);
    const displayUsd = principalWithdrawal || displayType === LoopDisplayType.UsdCostBasis;

    const profitLossDetails = useProfitLossDetails({
        gainSummary,
        displayUsd
    });

    if (!referral?.userReferralCode || !loopPosition || !gainSummary || !loopPosition?.loopExpanded?.inputToken)
        return undefined;

    if (!profitLossDetails) return undefined;
    const { profitLoss, profitLossPct } = profitLossDetails;

    const token = loopPosition.loopExpanded.inputToken;
    const currentApy = loopPosition.userLoopInfo.netApyLive;

    if (profitLoss === undefined || profitLossPct === undefined) return undefined;

    /* Uncomment desired stats */
    const stats: SocialShareStat[] = [
        {
            label: "P&L",
            value: formatPercent(profitLossPct, {
                includePlus: false
            }),
            headline: true,
            isNegative: profitLossPct < 0
        }
    ];

    if (currentApy) {
        stats.push({
            label: "Loop APY",
            value: formatPercent(currentApy, {
                includePlus: false
            }),
            headline: true,
            isNegative: currentApy < 0
        });
    }

    if (referral.userReferralCode) {
        stats.push({
            label: "Ref Code",
            value: `${BS_SHORT_URL_INVITE}/${referral.userReferralCode}`
        });
    }

    const leverage = calculateLeverageMultiplier(loopPosition) ?? 0;
    const isShort = getLeverageLoopDirection(loopPosition.loopExpanded) === "Short";
    const positionLabel = isShort ? `Short ${formatLeverage(leverage - 1)}` : `Long ${formatLeverage(leverage)}`;

    return {
        position: {
            image: token.imageUrl,
            name: token.symbol,
            label: positionLabel
        },
        stats,
        refCode: referral.userReferralCode
    };
}

export function useVaultSocialShareProps(
    vaultPosition: UserVaultPositionExpanded | undefined,
    userVaultPnl: UserVaultPnl | undefined
): SocialShareDialogProps | undefined {
    const { data: referral } = useUserReferral();
    const currentApy = useVaultApyBreakdown({ lendingVaultExpanded: vaultPosition?.vaultExpanded });

    const vaultName = vaultPosition?.vaultExpanded.vaultMetadata.name;
    const principalToken = vaultPosition?.vaultExpanded.principalMetadata;
    const principalPrice = vaultPosition?.vaultExpanded.principalOracle.usdPrice;

    const balanceUsd = vaultPosition?.totalAmount.amountUsd;
    const interestEarned = userVaultPnl?.currentPnl;

    if (
        interestEarned === undefined ||
        balanceUsd === undefined ||
        currentApy === undefined ||
        !referral ||
        !vaultName ||
        !principalToken ||
        !principalPrice
    )
        return undefined;

    const interestEarnedUsd = interestEarned * principalPrice;
    const returnPct = calculatePercentChange(balanceUsd - interestEarnedUsd, balanceUsd);

    const stats: SocialShareStat[] = [
        {
            label: "Position Return",
            value: formatPercent(returnPct, {
                includePlus: false
            }),
            headline: true,
            isNegative: returnPct < 0
        },
        {
            label: "Lend APY",
            value: formatPercent(currentApy.totalApy, {
                includePlus: false
            }),
            headline: true,
            isNegative: currentApy.totalApy < 0
        },
        {
            label: "Referral Code",
            value: `${BS_SHORT_URL_INVITE}/${referral.userReferralCode}`
        }
    ];

    return {
        position: {
            image: principalToken.imageUrl,
            name: principalToken.symbol,
            label: vaultName
        },
        stats,
        refCode: referral.userReferralCode
    };
}
